import React from "react";
import { Flame } from "lucide-react";
import { SpecialOffer } from "../types";

interface SpecialOffersProps {
  offers: SpecialOffer[];
}

export const SpecialOffers: React.FC<SpecialOffersProps> = ({ offers }) => {
  return (
    <section className="py-16 bg-gradient-to-r from-pink-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header - More playful */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <span className="text-4xl mr-2 animate-bounce">🔥</span>
            <Flame className="h-8 w-8 text-orange-500 mr-3 animate-pulse" />
            <h2 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 bg-clip-text text-transparent">
              Special Offers
            </h2>
            <Flame
              className="h-8 w-8 text-orange-500 ml-3 animate-pulse"
              style={{ animationDelay: "0.5s" }}
            />
            <span
              className="text-4xl ml-2 animate-bounce"
              style={{ animationDelay: "0.3s" }}
            >
              💥
            </span>
          </div>
          <p className="text-lg font-semibold text-gray-700 max-w-2xl mx-auto">
            🎊 Don't miss out on these amazing deals! 🎊
          </p>
        </div>

        {/* Offers Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {offers.map((offer) => (
            <div
              key={offer.id}
              className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover-scale-playful overflow-hidden group border-2 border-transparent hover:border-orange-300"
            >
              {/* Offer Image */}
              <div className="relative overflow-hidden">
                <img
                  src={offer.image}
                  alt={offer.title}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-300"
                />

                {/* Discount Badge - More playful */}
                <div className="absolute top-4 right-4 bg-gradient-to-r from-red-500 to-pink-500 text-white font-extrabold px-4 py-2 rounded-full text-lg shadow-2xl animate-bounce-gentle border-2 border-white">
                  🎉 {offer.discountPercentage}% OFF 🎉
                </div>

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>

              {/* Offer Content */}
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors duration-200">
                  {offer.title}
                </h3>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {offer.description}
                </p>

                {/* Validity */}
                <div className="mb-6">
                  <p className="text-sm text-gray-500">
                    Valid until: {offer.validUntil.toLocaleDateString()}
                  </p>
                </div>

                {/* Shop Now Button - More playful */}
                <button className="w-full bg-gradient-to-r from-orange-400 via-red-500 to-orange-600 hover:from-orange-500 hover:via-red-600 hover:to-orange-700 text-white font-bold py-4 px-6 rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-110 hover-bounce transition-all duration-300 border-2 border-white/50">
                  🛒 Shop Now 🎁
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Offer Info */}
        <div className="text-center mt-12">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-extrabold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-4">
              🎉 Limited Time Offers 🎉
            </h3>
            <p className="text-gray-700 mb-6 font-semibold">
              ⏰ These special deals won't last long! Shop now to secure your
              favorite toys at amazing prices! ⏰
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
              <span>✓ Free shipping on orders over ₹2,000</span>
              <span>✓ Secure payment processing</span>
              <span>⚠️ No warranty, no exchange policy</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpecialOffers;
